<div class="flex h-full min-h-0 w-full flex-col overflow-y-auto scrollbar-transparent">
  <div class="shrink-0 space-y-4 p-4">
    <div>
      <h4 class="text-sm font-semibold text-primary">Brief tracker</h4>
      <p class="mt-1 text-xs text-secondary">
        Look up a creator and brief to inspect application status, status history, shout brief config, and
        submitted posts. GodMode only.
      </p>
    </div>

    <div class="grid w-full max-w-3xl gap-4 sm:grid-cols-2">
      @if (!settingsContext.hasBoth()) {
      <p class="sm:col-span-2 text-sm text-amber-800 dark:text-amber-200" role="status">
        Enter brief ID and creator Firebase UID in the context bar above, then run the lookup.
      </p>
      }

      <div class="sm:col-span-2">
        <button
          type="button"
          class="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
          (click)="runSearch()"
          [disabled]="searchLoading() || !settingsContext.hasBoth()"
        >
          @if (searchLoading()) {
            Tracking…
          } @else {
            Track brief
          }
        </button>
      </div>
    </div>

    @if (searchError()) {
    <p class="text-sm text-red-600 dark:text-red-400" role="alert">{{ searchError() }}</p>
    }
  </div>

  @if (lookupResult(); as result) {
  @let ask = summariseBriefAsk(result.brief ?? {});
  <div class="shrink-0 space-y-4 border-t border-zinc-200 p-4 dark:border-zinc-800">
    @if (needsCompletionCheck() || completionCheckNotice()) {
    <div
      class="rounded-xl border border-amber-300/70 bg-amber-50 p-4 dark:border-amber-700/50 dark:bg-amber-950/30"
      role="status"
    >
      <p class="text-sm font-medium text-amber-950 dark:text-amber-50">Completion check</p>
      @if (completionCheckNotice()) {
      <p class="mt-1 text-sm text-amber-900 dark:text-amber-100">{{ completionCheckNotice() }}</p>
      } @else if (needsCompletionCheck()) {
      <p class="mt-1 text-sm text-amber-900 dark:text-amber-100">
        Posted history exists but application status is still ReadyToPost (4). A completion check will
        run automatically, or you can trigger it again below.
      </p>
      }
      @if (needsCompletionCheck()) {
      <button
        type="button"
        class="mt-3 rounded-lg border border-amber-400 bg-white px-3 py-2 text-sm font-medium text-amber-950 hover:bg-amber-100 disabled:opacity-50 dark:border-amber-600 dark:bg-amber-950 dark:text-amber-50 dark:hover:bg-amber-900"
        (click)="runCompletionCheckManually()"
        [disabled]="completionCheckLoading() || searchLoading()"
      >
        @if (completionCheckLoading()) {
          Running completion check…
        } @else {
          Re-run completion check
        }
      </button>
      }
    </div>
    }

    <section class="rounded-xl border border-zinc-200 bg-white p-4 dark:border-zinc-800 dark:bg-zinc-950">
      <h5 class="text-sm font-semibold text-primary">Brief &amp; creator</h5>
      <dl class="mt-3 grid gap-2 text-sm sm:grid-cols-2">
        <div>
          <dt class="font-medium text-secondary">Creator</dt>
          <dd class="text-primary">{{ result.creatorFullName }}</dd>
        </div>
        <div>
          <dt class="font-medium text-secondary">Brief title</dt>
          <dd class="text-primary">{{ result.briefTitle }}</dd>
        </div>
        <div>
          <dt class="font-medium text-secondary">Brief created</dt>
          <dd class="text-primary">{{ result.briefCreatedDate ?? '—' }}</dd>
        </div>
        <div>
          <dt class="font-medium text-secondary">Visibility</dt>
          <dd class="text-primary">{{ result.briefVisibility }}</dd>
        </div>
        <div class="sm:col-span-2">
          <dt class="font-medium text-secondary">Brief type</dt>
          <dd>
            <p class="m-0 text-primary">{{ ask.source === 'pieces' ? ask.label : result.briefType }}</p>
            @if (ask.source === 'pieces') {
            <p class="m-0 mt-1 text-sm text-primary">{{ ask.detail }}</p>
            } @else {
            @let platformGroups = shoutBriefPlatformGroups(result.shoutBrief);
            @if (platformGroups.primary; as mainPlatform) {
            <p class="mt-3 mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-secondary">
              Main platform
            </p>
            <ul class="space-y-2">
              <li [class]="shoutBriefPlatformRowClass(true)">
                @if (mainPlatform.logoUrl) {
                <img [ngSrc]="mainPlatform.logoUrl" width="24" height="24" alt=""
                  class="size-6 shrink-0 rounded-md object-cover" />
                }
                <p class="m-0 min-w-0 flex-1 text-sm font-medium text-primary">{{ mainPlatform.label }}</p>
                <p [class]="shoutBriefPlatformBadgeClass(true)">Main</p>
              </li>
            </ul>
            }
            @if (platformGroups.additional.length > 0) {
            <p class="mt-3 mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-secondary">
              Additional platforms
            </p>
            <ul class="space-y-2">
              @for (platform of platformGroups.additional; track platform.id) {
              <li [class]="shoutBriefPlatformRowClass(false)">
                @if (platform.logoUrl) {
                <img [ngSrc]="platform.logoUrl" width="24" height="24" alt=""
                  class="size-6 shrink-0 rounded-md object-cover" />
                }
                <p class="m-0 min-w-0 flex-1 text-sm font-medium text-primary">{{ platform.label }}</p>
                <p [class]="shoutBriefPlatformBadgeClass(false)">Additional</p>
              </li>
              }
            </ul>
            }
            }
          </dd>
        </div>
        <div>
          <dt class="font-medium text-secondary">shoutBriefId</dt>
          <dd class="font-mono text-xs break-all text-primary">
            {{ result.brief?.shoutBriefId ?? result.brief?.shoutBrief?.id ?? '—' }}
          </dd>
        </div>
      </dl>
    </section>

    <section class="rounded-xl border border-zinc-200 bg-white p-4 dark:border-zinc-800 dark:bg-zinc-950">
      <h5 class="text-sm font-semibold text-primary">Brief application</h5>
      @if (result.application; as app) {
      <dl class="mt-3 grid gap-2 text-sm sm:grid-cols-2">
        <div class="sm:col-span-2">
          <dt class="font-medium text-secondary">Application ID</dt>
          <dd class="font-mono text-xs break-all text-primary">{{ app.id }}</dd>
        </div>
        <div>
          <dt class="font-medium text-secondary">Status</dt>
          <dd class="text-primary">
            {{ applicationStatusLabel(app.applicationStatus) }}
            <span class="text-secondary">({{ app.applicationStatus }})</span>
          </dd>
        </div>
        <div>
          <dt class="font-medium text-secondary">Status updated</dt>
          <dd class="text-primary">{{ formatDate(app.applicationStatusModifiedDate) }}</dd>
        </div>
      </dl>
      @if (canUnhireApplication(app.applicationStatus, true)) {
      <div class="mt-4 flex flex-wrap items-center gap-2">
        <button
          type="button"
          class="inline-flex items-center gap-x-1.5 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm font-medium text-red-800 hover:bg-red-100 disabled:opacity-50 dark:border-red-500/40 dark:bg-red-500/10 dark:text-red-200 dark:hover:bg-red-500/20"
          [disabled]="unhireLoading() || searchLoading()"
          (click)="unhireApplication()"
        >
          @if (unhireLoading()) {
            Un-hiring…
          } @else {
            Un-hire
          }
        </button>
        <p class="m-0 text-xs text-secondary">
          Sets status to Hired removed (13) and releases the wallet reservation.
        </p>
      </div>
      }
      @if (canRestoreUnhiredToApplied(app.applicationStatus)) {
      <div class="mt-4 flex flex-wrap items-center gap-2">
        <button
          type="button"
          class="btn-shout btn-shout-sm btn-shout-primary shrink-0"
          [disabled]="restoreToAppliedLoading() || searchLoading()"
          (click)="restoreUnhiredToApplied()"
        >
          @if (restoreToAppliedLoading()) {
            Moving back…
          } @else {
            Move back to Applied
          }
        </button>
        <p class="m-0 text-xs text-secondary">
          Sets status to Applied (2), clears the decline reason, and releases a live wallet reservation if one is still open.
        </p>
      </div>
      }
      } @else {
      <p class="mt-2 text-sm text-secondary">No brief application found for this creator and brief.</p>
      }
    </section>

    <section
      class="rounded-xl border p-4"
      [class]="
        result.videoChecklist.meetsTarget
          ? 'border-emerald-300/70 bg-emerald-50/80 dark:border-emerald-700/50 dark:bg-emerald-950/20'
          : 'border-red-300/70 bg-red-50/80 dark:border-red-700/50 dark:bg-red-950/20'
      "
    >
      <div class="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h5 class="text-sm font-semibold text-primary">Video checklist</h5>
          <p class="mt-0.5 text-xs text-secondary">
            @if (ask.source === 'pieces') {
              {{ ask.label }} · {{ ask.detail }}
            } @else {
              videosPerCreator vs accepted videos for this creator and brief (all uploads listed below).
            }
          </p>
        </div>
        <span
          class="rounded-full px-2.5 py-0.5 text-xs font-medium"
          [class]="
            result.videoChecklist.meetsTarget
              ? 'bg-emerald-200 text-emerald-950 dark:bg-emerald-900 dark:text-emerald-50'
              : 'bg-red-200 text-red-950 dark:bg-red-900 dark:text-red-50'
          "
        >
          {{ result.videoChecklist.videoCount }} / {{ result.videoChecklist.videosPerCreator }}
          {{ result.videoChecklist.meetsTarget ? '— OK' : '— mismatch' }}
        </span>
      </div>

      @if (result.videoChecklist.videos.length) {
      <ul class="mt-3 space-y-2">
        @for (video of result.videoChecklist.videos; track video.id) {
        <li
          class="rounded-lg border border-zinc-200/80 bg-white/80 p-3 text-sm dark:border-zinc-700 dark:bg-zinc-950/60"
        >
          <dl class="grid gap-1 sm:grid-cols-2">
            <div class="sm:col-span-2">
              <dt class="inline font-medium text-secondary">Name: </dt>
              <dd class="inline text-primary">{{ video.videoName }}</dd>
            </div>
            <div class="sm:col-span-2">
              <dt class="inline font-medium text-secondary">fullUrl: </dt>
              <dd class="inline break-all text-primary">{{ video.fullUrl ?? '—' }}</dd>
            </div>
            <div>
              <dt class="inline font-medium text-secondary">Status: </dt>
              <dd class="inline text-primary">
                {{ video.statusLabel }}
                <span class="text-secondary">({{ video.status }})</span>
              </dd>
            </div>
            <div>
              <dt class="inline font-medium text-secondary">Video ID: </dt>
              <dd class="inline font-mono text-xs break-all text-primary">{{ video.id }}</dd>
            </div>
          </dl>
        </li>
        }
      </ul>
      } @else {
      <p class="mt-2 text-sm text-secondary">No videos found for this creator and brief.</p>
      }
    </section>

    <section class="rounded-xl border border-zinc-200 bg-white p-4 dark:border-zinc-800 dark:bg-zinc-950">
      <h5 class="text-sm font-semibold text-primary">Application status history</h5>
      <p class="mt-0.5 text-xs text-secondary">Sorted by created date, oldest first.</p>
      @if (result.statusHistory.length) {
      <ul class="mt-3 space-y-2">
        @for (row of result.statusHistory; track row.id) {
        <li
          class="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 rounded-lg border border-zinc-100 bg-zinc-50 px-3 py-2 text-sm dark:border-zinc-800 dark:bg-zinc-900/50"
        >
          <span class="font-medium text-primary">{{ formatDate(row.createdDate) }}</span>
          <span>{{ applicationStatusLabel(row.status) }}</span>
          <span class="text-secondary">({{ row.status }})</span>
          @if (row.video_name) {
          <span class="text-xs text-secondary">— {{ row.video_name }}</span>
          }
          @if (isStatusHistoryUserStatus(row.status)) {
          <span class="text-xs text-secondary">— {{ statusHistoryUserLabel(row) }}</span>
          }
        </li>
        }
      </ul>
      } @else {
      <p class="mt-2 text-sm text-secondary">No status history rows.</p>
      }
    </section>

    @if (result.brief?.shoutBriefId || result.brief?.shoutBrief?.id || result.shoutBrief) {
    <section class="rounded-xl border border-zinc-200 bg-white p-4 dark:border-zinc-800 dark:bg-zinc-950">
      <h5 class="text-sm font-semibold text-primary">Brief shout</h5>
      @if (result.shoutBrief; as shout) {
      <dl class="mt-3 grid gap-2 text-sm sm:grid-cols-2">
        <div class="sm:col-span-2">
          <dt class="font-medium text-secondary">suggestedHastags</dt>
          <dd class="wrap-break-word text-primary">{{ shout.suggestedHastags ?? '—' }}</dd>
        </div>
        @if (ask.source === 'pieces') {
        <div class="sm:col-span-2">
          <dt class="font-medium text-secondary">Pieces</dt>
          <dd>
            <p class="m-0 text-primary">{{ ask.label }}</p>
            <p class="m-0 mt-1 text-sm text-secondary">{{ ask.detail }}</p>
          </dd>
        </div>
        } @else {
        <div class="sm:col-span-2">
          <dt class="font-medium text-secondary">socialPlatform</dt>
          <dd>
            @let shoutPlatforms = shoutBriefPlatformGroups(shout);
            @if (shoutPlatforms.primary; as mainPlatform) {
            <p class="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-secondary">
              Main platform
            </p>
            <ul class="space-y-2">
              <li [class]="shoutBriefPlatformRowClass(true)">
                @if (mainPlatform.logoUrl) {
                <img [ngSrc]="mainPlatform.logoUrl" width="24" height="24" alt=""
                  class="size-6 shrink-0 rounded-md object-cover" />
                }
                <div class="min-w-0 flex-1">
                  <p class="m-0 text-sm font-medium text-primary">{{ mainPlatform.label }}</p>
                  <p class="m-0 text-xs text-secondary">{{ mainPlatform.id }}</p>
                </div>
                <p [class]="shoutBriefPlatformBadgeClass(true)">Main</p>
              </li>
            </ul>
            } @else {
            <p class="m-0 text-primary">—</p>
            }
          </dd>
        </div>
        <div class="sm:col-span-2">
          <dt class="font-medium text-secondary">crossPlatforms</dt>
          <dd>
            @let extraShoutPlatforms = shoutBriefPlatformGroups(shout).additional;
            @if (extraShoutPlatforms.length > 0) {
            <p class="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-secondary">
              Additional platforms
            </p>
            <ul class="space-y-2">
              @for (platform of extraShoutPlatforms; track platform.id) {
              <li [class]="shoutBriefPlatformRowClass(false)">
                @if (platform.logoUrl) {
                <img [ngSrc]="platform.logoUrl" width="24" height="24" alt=""
                  class="size-6 shrink-0 rounded-md object-cover" />
                }
                <div class="min-w-0 flex-1">
                  <p class="m-0 text-sm font-medium text-primary">{{ platform.label }}</p>
                  <p class="m-0 text-xs text-secondary">{{ platform.id }}</p>
                </div>
                <p [class]="shoutBriefPlatformBadgeClass(false)">Additional</p>
              </li>
              }
            </ul>
            } @else {
            <p class="m-0 text-primary">—</p>
            }
          </dd>
        </div>
        }
        <div>
          <dt class="font-medium text-secondary">resultsVersion</dt>
          <dd class="text-primary">{{ shout.resultsVersion }}</dd>
        </div>
      </dl>
      } @else {
      <p class="mt-2 text-sm text-amber-700 dark:text-amber-300">
        shoutBriefId is set but the brief_shout record could not be loaded.
      </p>
      }
    </section>
    }

    <section class="rounded-xl border border-zinc-200 bg-white p-4 dark:border-zinc-800 dark:bg-zinc-950">
      <h5 class="text-sm font-semibold text-primary">Submitted posts</h5>
      <p class="mt-0.5 text-xs text-secondary">
        Only <span class="font-medium text-primary">Valid</span> posts count toward completion check.
        Orchestrator refresh does not re-validate — use Re-validate when status is not Valid, or Force validate to skip checks (including extra-platform posts after the application is already Posted).
      </p>
      @let postChecklist = buildSubmittedPostChecklist(result);
      @if (postChecklist.applies) {
      <div class="mt-3 flex flex-wrap items-center justify-between gap-2">
        <p class="m-0 text-xs text-secondary">
          @if (ask.source === 'pieces') {
            {{ ask.label }} · {{ ask.detail }}
          } @else {
          Required on {{ postChecklist.platformLabel }} (platform {{ postChecklist.platform }}):
          <span class="font-medium text-primary">{{ postChecklist.videosPerCreator }}</span>
          valid post{{ postChecklist.videosPerCreator === 1 ? '' : 's' }} (videosPerCreator).
          }
        </p>
        <span
          class="rounded-full px-2.5 py-0.5 text-xs font-medium"
          [class]="
            postChecklist.meetsTarget
              ? 'bg-emerald-200 text-emerald-950 dark:bg-emerald-900 dark:text-emerald-50'
              : 'bg-red-200 text-red-950 dark:bg-red-900 dark:text-red-50'
          "
        >
          {{ postChecklist.validPostCount }} / {{ postChecklist.videosPerCreator }} valid
          {{ postChecklist.meetsTarget ? '— OK' : '— short' }}
        </span>
      </div>
      @if (!postChecklist.meetsTarget) {
      <div
        class="mt-3 rounded-lg border border-amber-300/70 bg-amber-50 p-3 dark:border-amber-700/50 dark:bg-amber-950/30"
        role="alert"
      >
        <p class="m-0 text-sm font-medium text-amber-950 dark:text-amber-50">
          Not enough valid posts for completion
        </p>
        <p class="m-0 mt-1 text-sm text-amber-900 dark:text-amber-100">
          @if (ask.source === 'pieces') {
            This brief asks for {{ ask.label }} ({{ ask.detail }}).
          } @else {
          This brief requires {{ postChecklist.videosPerCreator }} valid
          {{ postChecklist.platformLabel }} post{{ postChecklist.videosPerCreator === 1 ? '' : 's' }}.
          }
          This creator has {{ postChecklist.validPostCount }} valid
          ({{ postChecklist.submittedPostCountOnPlatform }} submitted on
          {{ postChecklist.platformLabel }}). The application cannot advance to Posted until all
          required posts are submitted and validated on the brief platform.
        </p>
      </div>
      }
      }
      @if (canOfferCreateSubmittedPost(result.application?.applicationStatus, result.shoutBrief, result.videoChecklist.videos, result.submittedPosts)) {
      <div class="mt-3">
        @if (!createPanelOpen()) {
        <button
          type="button"
          class="btn-shout btn-shout-sm btn-shout-primary"
          (click)="openCreateSubmittedPostPanel()"
        >
          Create submitted post
        </button>
        } @else {
        <div class="rounded-lg border border-zinc-200 bg-zinc-50 p-3 dark:border-zinc-800 dark:bg-zinc-900/50">
          <h6 class="m-0 text-sm font-semibold text-primary">Create submitted post</h6>
          <p class="m-0 mt-1 text-xs text-secondary">
            Pick an approved video and a live social post. Validation runs after create. When the
            required posts are Valid, the application moves to Posted and the hire payout runs.
          </p>

          @if (createChannelChoices().length > 1) {
          <label class="mt-3 block text-sm font-medium text-secondary" for="create-post-channel">Platform</label>
          <select
            id="create-post-channel"
            class="mt-1 block w-full rounded-lg border border-zinc-200 bg-white px-3 py-2 text-sm text-primary shadow-sm dark:border-zinc-700 dark:bg-zinc-950"
            [value]="createSocialChannel()"
            (change)="onCreateSocialChannelChange($any($event.target).value)"
            [disabled]="createLoading()"
          >
            @for (channel of createChannelChoices(); track channel.id) {
            <option [value]="channel.id">{{ channel.label }} ({{ channel.id }})</option>
            }
          </select>
          }

          <label class="mt-3 block text-sm font-medium text-secondary" for="create-post-video">Approved video</label>
          <select
            id="create-post-video"
            class="mt-1 block w-full rounded-lg border border-zinc-200 bg-white px-3 py-2 text-sm text-primary shadow-sm dark:border-zinc-700 dark:bg-zinc-950"
            [value]="createVideoId()"
            (change)="onCreateVideoChange($any($event.target).value)"
            [disabled]="createLoading()"
          >
            @for (video of createVideoChoices(); track video.id) {
            <option [value]="video.id">{{ video.videoName }}</option>
            }
          </select>

          <p class="m-0 mt-3 text-sm font-medium text-secondary">Super Social posts</p>
          @if (createCandidatesLoading()) {
          <p class="m-0 mt-1 text-sm text-secondary">Loading posts…</p>
          } @else if (createCandidatesError(); as candidateError) {
          <p class="m-0 mt-1 text-sm text-amber-800 dark:text-amber-200">{{ candidateError }}</p>
          } @else if (createCandidatePosts().length === 0) {
          <p class="m-0 mt-1 text-sm text-secondary">
            No Super Social posts listed for this platform. Paste a post URL below, or refresh Super Socials and try again.
          </p>
          } @else {
          <ul class="mt-2 max-h-64 space-y-2 overflow-y-auto">
            @for (candidate of createCandidatePosts(); track candidate.id) {
            <li>
              <label
                [class]="
                  createSelectedPostId() === candidate.id
                    ? 'flex cursor-pointer items-start gap-3 rounded-lg border border-emerald-400 bg-white p-2 dark:border-emerald-600 dark:bg-zinc-950'
                    : 'flex cursor-pointer items-start gap-3 rounded-lg border border-zinc-200 bg-white p-2 dark:border-zinc-700 dark:bg-zinc-950'
                "
              >
                <input
                  type="radio"
                  name="create-candidate-post"
                  class="mt-1"
                  [checked]="createSelectedPostId() === candidate.id"
                  (change)="selectCreateCandidatePost(candidate.id)"
                  [disabled]="createLoading()"
                />
                @if (candidate.thumbnailUrl) {
                <img
                  [src]="candidate.thumbnailUrl"
                  alt=""
                  class="size-12 shrink-0 rounded-md object-cover"
                />
                }
                <div class="min-w-0 flex-1">
                  <p class="m-0 text-sm text-primary">{{ candidate.title }}</p>
                  @if (candidate.postedAtLabel) {
                  <p class="m-0 text-xs text-secondary">{{ candidate.postedAtLabel }}</p>
                  }
                </div>
              </label>
            </li>
            }
          </ul>
          }

          <label class="mt-3 block text-sm font-medium text-secondary" for="create-post-manual-link">
            Or paste a post URL
          </label>
          <input
            id="create-post-manual-link"
            type="url"
            class="mt-1 block w-full rounded-lg border border-zinc-200 bg-white px-3 py-2 text-sm text-primary shadow-sm dark:border-zinc-700 dark:bg-zinc-950"
            [value]="createManualLink()"
            (input)="onCreateManualLinkInput($any($event.target).value)"
            [disabled]="createLoading()"
            placeholder="https://"
          />

          <label class="mt-3 block text-sm font-medium text-secondary" for="create-post-ad-code">Ad code</label>
          <input
            id="create-post-ad-code"
            type="text"
            class="mt-1 block w-full rounded-lg border border-zinc-200 bg-white px-3 py-2 text-sm text-primary shadow-sm dark:border-zinc-700 dark:bg-zinc-950"
            [value]="createAdCode()"
            (input)="onCreateAdCodeInput($any($event.target).value)"
            [disabled]="createLoading()"
          />

          <div class="mt-4 flex flex-wrap justify-end gap-2">
            <button
              type="button"
              class="btn-shout btn-shout-sm btn-shout-secondary"
              (click)="closeCreateSubmittedPostPanel()"
              [disabled]="createLoading()"
            >
              Cancel
            </button>
            <button
              type="button"
              class="btn-shout btn-shout-sm btn-shout-primary"
              (click)="submitCreateSubmittedPost()"
              [disabled]="createLoading()"
            >
              {{ createLoading() ? 'Creating…' : 'Create and validate' }}
            </button>
          </div>
        </div>
        }
      </div>
      }
      @if (result.submittedPostsFound) {
      <ul class="mt-3 space-y-3">
        @for (post of result.submittedPosts; track post.id) {
        <li class="relative rounded-lg border border-zinc-100 bg-zinc-50 p-3 pr-36 text-sm dark:border-zinc-800 dark:bg-zinc-900/50">
          <div
            [class]="
              submittedPostStatusPillClass(post.status) +
              ' absolute right-3 top-3 max-w-34 text-right'
            "
          >{{ post.status }}</div>
          @if (canRevalidateSubmittedPost(post.status)) {
          <div class="mb-2 flex flex-wrap items-center gap-2">
            @if (isSubmittedPostValidateLoading(post.id)) {
            <span class="inline-flex items-center gap-1.5 text-xs text-secondary">
              <svg
                aria-hidden="true"
                class="size-3.5 animate-spin text-secondary"
                viewBox="0 0 100 101"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                  fill="currentColor"
                />
              </svg>
              Validating…
            </span>
            } @else {
            <button
              type="button"
              class="btn-shout btn-shout-xs btn-shout-primary inline-flex items-center gap-x-1.5"
              (click)="revalidateSubmittedPost(post)"
            >
              Re-validate post
            </button>
            @if (canForceValidateSubmittedPost(post.status, result.application?.applicationStatus)) {
            <button
              type="button"
              class="inline-flex items-center gap-x-1.5 rounded-md border border-red-200 bg-red-50 px-2.5 py-1.5 text-xs font-medium text-red-800 hover:bg-red-100 dark:border-red-500/40 dark:bg-red-500/10 dark:text-red-200 dark:hover:bg-red-500/20"
              [disabled]="isSubmittedPostForceValidateLoading(post.id)"
              (click)="forceValidateSubmittedPost(post)"
            >
              @if (isSubmittedPostForceValidateLoading(post.id)) {
              Forcing…
              } @else {
              Force validate
              }
            </button>
            }
            }
          </div>
          }
          <dl class="grid gap-1 sm:grid-cols-2">
            <div class="sm:col-span-2">
              <dt class="inline font-medium text-secondary">ID: </dt>
              <dd class="inline font-mono text-xs break-all text-primary">{{ post.id }}</dd>
            </div>
            <div>
              <dt class="inline font-medium text-secondary">briefId: </dt>
              <dd class="inline font-mono text-xs break-all text-primary">{{ post.briefId }}</dd>
            </div>
            <div>
              <dt class="inline font-medium text-secondary">Social channel: </dt>
              <dd class="inline text-primary">
                {{ socialPlatformLabel(post.socialChannel) }} ({{ post.socialChannel }})
              </dd>
            </div>
            @let handleMatch = getSubmittedPostHandleMatch(post, result.superSocial);
            <div class="sm:col-span-2 flex flex-wrap items-center gap-2">
              <span>
                <span class="font-medium text-secondary"
                  >{{ superSocialIdentityLabelForChannel(post.socialChannel) }}:
                </span>
                <span class="text-primary">{{ handleMatch.extractedHandle ?? '—' }}</span>
                @if (handleMatch.superSocialHandle) {
                <span class="text-secondary">
                  (Super Socials:
                  <span class="text-primary">{{ handleMatch.superSocialHandle }}</span
                  >)
                </span>
                }
              </span>
              @if (handleMatch.status === 'match') {
              <div class="pill pill-green">Matches Super Socials</div>
              } @else if (handleMatch.status === 'mismatch') {
              <div class="pill pill-rose">Does NOT match Super Socials</div>
              } @else {
              <div class="pill pill-amber">Cannot verify from URL</div>
              }
            </div>
            <div class="sm:col-span-2">
              <label class="block text-sm font-medium text-secondary" [for]="'video-link-' + post.id"
                >videoLink</label
              >
              <input
                [id]="'video-link-' + post.id"
                type="url"
                class="mt-1 block w-full rounded-lg border border-zinc-200 bg-white px-3 py-2 text-sm text-primary shadow-sm dark:border-zinc-700 dark:bg-zinc-950"
                [value]="getSubmittedPostEditState(post).videoLink"
                (input)="onVideoLinkInput(post, $any($event.target).value)"
                [disabled]="isSubmittedPostEditLoading(post.id)"
              />
              @if (getVideoLinkResolveError(post.id); as resolveError) {
              <p class="mt-1 text-xs text-amber-800 dark:text-amber-200" role="status">{{ resolveError }}</p>
              } @else {
              <p class="mt-1 text-xs text-secondary">
                fullUrl preview updates as you type; saved with Save post fields.
              </p>
              }
            </div>
            <div class="sm:col-span-2">
              <span
                class="block text-sm font-medium text-secondary"
                [id]="'full-url-label-' + post.id"
                >fullUrl</span
              >
              @if (isVideoLinkResolveLoading(post.id)) {
              <div
                class="full-url-skeleton-line mt-1"
                role="status"
                aria-live="polite"
                [attr.aria-labelledby]="'full-url-label-' + post.id"
                aria-busy="true"
                aria-label="Generating fullUrl from videoLink"
              ></div>
              } @else if (getDisplayedFullUrl(post); as displayedFullUrl) {
              <p
                class="mt-1 text-sm text-primary break-all"
                [attr.aria-labelledby]="'full-url-label-' + post.id"
                >{{ displayedFullUrl }}</p
              >
              @if (hasUnsavedFullUrlPreview(post)) {
              <p class="mt-1 text-xs text-secondary">Unsaved preview — click Save post fields to persist.</p>
              } @else {
              <p class="mt-1 text-xs text-secondary">Read-only — derived from videoLink.</p>
              }
              } @else {
              <p
                class="mt-1 text-sm text-secondary italic"
                [attr.aria-labelledby]="'full-url-label-' + post.id"
                >Enter or update videoLink to generate fullUrl.</p
              >
              <p class="mt-1 text-xs text-secondary">Read-only — derived from videoLink.</p>
              }
            </div>
            <div class="sm:col-span-2">
              <label class="block text-sm font-medium text-secondary" [for]="'ad-code-' + post.id"
                >adCode</label
              >
              <input
                [id]="'ad-code-' + post.id"
                type="text"
                class="mt-1 block w-full rounded-lg border border-zinc-200 bg-white px-3 py-2 text-sm text-primary shadow-sm dark:border-zinc-700 dark:bg-zinc-950"
                [value]="getSubmittedPostEditState(post).adCode"
                (input)="updateSubmittedPostField(post, 'adCode', $any($event.target).value)"
                [disabled]="isSubmittedPostEditLoading(post.id)"
              />
            </div>
            <div class="sm:col-span-2 mt-4 flex justify-end">
              <button
                type="button"
                class="rounded-lg bg-primary px-3 py-2 text-sm font-medium text-white disabled:opacity-50"
                (click)="saveSubmittedPostEdits(post)"
                [disabled]="isSubmittedPostEditLoading(post.id) || isVideoLinkResolveLoading(post.id)"
              >
                @if (isSubmittedPostEditLoading(post.id)) {
                  Saving…
                } @else {
                  Save post fields
                }
              </button>
            </div>
            <div>
              <dt class="inline font-medium text-secondary">resultsVersion: </dt>
              <dd class="inline text-primary">{{ post.resultsVersion ?? '—' }}</dd>
            </div>
          </dl>
        </li>
        }
      </ul>
      } @else {
      <p class="mt-2 text-sm text-amber-700 dark:text-amber-300">
        No submitted post records found for this brief application.
      </p>
      }
    </section>

    <section class="rounded-xl border border-zinc-200 bg-white p-4 dark:border-zinc-800 dark:bg-zinc-950">
      <h5 class="text-sm font-semibold text-primary">Super Socials</h5>
      <p class="mt-0.5 text-xs text-secondary">
        Linked accounts at a glance. Refresh and edit handles in the Super Socials admin tool.
      </p>

      @if (!result.superSocial.found) {
      <p class="mt-2 text-sm text-amber-700 dark:text-amber-300">
        No SuperSocial creator found for this Shout ID and business.
      </p>
      } @else if (!result.superSocial.accounts.length) {
      <p class="mt-2 text-sm text-secondary">
        SuperSocial creator exists but has no connected Instagram, TikTok, or YouTube accounts.
      </p>
      } @else {
      <ul class="mt-3 space-y-2">
        @for (account of result.superSocial.accounts; track account.id) {
        <li
          class="flex items-center gap-3 rounded-lg border px-3 py-2 dark:border-zinc-800"
          [class.border-emerald-300]="isSuperSocialAccountMatched(account, result)"
          [class.bg-emerald-50]="isSuperSocialAccountMatched(account, result)"
          [class.dark:bg-emerald-950/20]="isSuperSocialAccountMatched(account, result)"
          [class.border-zinc-100]="!isSuperSocialAccountMatched(account, result)"
          [class.bg-zinc-50]="!isSuperSocialAccountMatched(account, result)"
          [class.dark:bg-zinc-900/50]="!isSuperSocialAccountMatched(account, result)"
        >
          @if (account.avatarUrl) {
          <img
            [src]="account.avatarUrl"
            [alt]="account.platformLabel + ' profile'"
            class="h-10 w-10 shrink-0 rounded-full border border-zinc-200 object-cover dark:border-zinc-700"
            width="40"
            height="40"
          />
          } @else {
          <div
            class="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-zinc-200 bg-zinc-100 text-xs font-medium text-secondary dark:border-zinc-700 dark:bg-zinc-800"
            aria-hidden="true"
          >
            {{ account.platformLabel.charAt(0) }}
          </div>
          }
          <div class="min-w-0 flex-1 flex flex-wrap items-center gap-2">
            <p class="text-sm font-medium text-primary">{{ account.platformLabel }}</p>
            @if (superSocialAccountHandleValue(account); as handle) {
            <div class="pill pill-blue">{{ handle }}</div>
            } @else {
            <div class="pill pill-blue">—</div>
            }
          </div>
          <p class="shrink-0 text-xs text-secondary">
            Updated date:
            <span [class]="superSocialUpdatedDateColour(account.updatedDateIso)">{{ account.updatedDate ?? '—' }}</span>
          </p>
        </li>
        }
      </ul>
      }
    </section>

      </div>
  }
</div>
